import Loader from "@components/Loader";
import { useAuthContext } from "@contexts/auth";
import { useDashboardUIActions } from "@contexts/dashboardUI";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { downloadCsv } from "@utils/downloadCsv";
import { formatNumberToCurrency } from "@utils/formatNumber";
import {
  combineMetricEnvelopes,
  metricCsvValue,
  metricPresentation,
  metricReasonStyle,
  metricValue,
  normalizeMetricEnvelope,
} from "@utils/metricPresentation";
import { useEffect, useMemo, useState } from "react";

const PAGE_SIZE = 50;
const EMPTY_META = { totalItems: 0, totalPages: 0, totalValue: null, totalValueMetric: null };

const finiteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Curve = () => {
  const { toggleItemDetailOffcanvas } = useDashboardUIActions();
  const { selectDetail, getResourceEAP, getCurve } = useProjectActions();
  const curveConfiguration = useAuthContext()?.user?.configurations?.curve ?? {};
  const { curveA = 80, curveB = 95 } = curveConfiguration;
  const { activeProject } = useProjectContext();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [type, setType] = useState("has_evaluated");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(EMPTY_META);
  const [error, setError] = useState("");

  const buildingWbs = useMemo(() => {
    const ids = activeProject?.wbs_code_ids;
    if (Array.isArray(ids) && ids.length > 0) return ids.filter(Boolean).join(",");
    return String(activeProject?.wbs_code_id || "").trim();
  }, [activeProject]);

  useEffect(() => {
    if (!buildingWbs) {
      setResources([]);
      setMeta(EMPTY_META);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    const loadCurve = async () => {
      try {
        const response = await getCurve(buildingWbs, type, {
          page,
          pageSize: PAGE_SIZE,
          search: searchString,
        });
        if (cancelled) return;

        const items = Array.isArray(response?.items) ? response.items : [];
        setResources(items);
        setMeta({
          totalItems: finiteNumber(response?.totalItems),
          totalPages: finiteNumber(response?.totalPages),
          totalValue: response?.totalValue ?? null,
          totalValueMetric: response?.metrics?.totalValue ?? null,
        });
      } catch (loadError) {
        if (cancelled) return;
        setResources([]);
        setMeta(EMPTY_META);
        setError(loadError?.message || "Não foi possível carregar a Curva ABC.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCurve();
    return () => {
      cancelled = true;
    };
  }, [buildingWbs, page, searchString, type]);

  const renderRow = (label, value) => (
    <div className="data">
      <span className="accordion-label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );

  const metricEnvelope = (resource, metricId, legacyField = metricId) => {
    if (resource.metrics?.[metricId]) return resource.metrics[metricId];
    const value = resource[legacyField];
    return {
      id: metricId,
      value: typeof value === "number" && Number.isFinite(value) ? value : null,
      status: typeof value === "number" && Number.isFinite(value)
        ? "ok"
        : "source_unavailable",
    };
  };

  const resourceIdentity = (resource) => (
    `${String(resource?.building_wbs ?? "unknown")}:${String(resource?.id ?? "unknown")}`
  );

  const renderMetricRow = (resource, label, envelope, formatter) => {
    const presented = metricPresentation(envelope, {
      formatter,
      reasonId: `curve-${resourceIdentity(resource).replace(/[^a-zA-Z0-9_-]/g, "-")}-${envelope?.id || "metric"}-reason`,
    });
    return renderRow(label, <>
      <span aria-describedby={presented.describedBy}>{presented.text}</span>
      {!presented.available && (
        <span id={presented.describedBy} style={metricReasonStyle}>{presented.reason}</span>
      )}
    </>);
  };

  const handleSearchString = (event) => {
    setPage(1);
    setSearchString(event.target.value);
  };

  const selectType = (nextType) => {
    setPage(1);
    setType(nextType);
  };

  const onShowDetail = (resource) => {
    if (!activeProject) return;
    selectDetail(resource, "resource");
    getResourceEAP({
      project_id: activeProject._id,
      id: resource.id,
      building_wbs: resource.building_wbs,
    });
    toggleItemDetailOffcanvas();
  };

  const selectedTotalField = type === "has_attended" ? "attended_total" : "evaluated_total";

  const renderRows = () => resources.map((resource) => {
    const quantityId = type === "has_attended" ? "attended_quantity" : "evaluated_quantity";
    const quantityEnvelope = metricEnvelope(resource, quantityId);
    const totalEnvelope = resource.metrics?.[selectedTotalField]
      ?? resource.metrics?.value
      ?? metricEnvelope(resource, selectedTotalField);
    const percentageEnvelope = metricEnvelope(resource, "percentage");
    const totalPercentageEnvelope = metricEnvelope(resource, "totalPercentage");
    const totalPercentage = metricValue(totalPercentageEnvelope);
    const unitEnvelope = combineMetricEnvelopes(
      [totalEnvelope, quantityEnvelope],
      ([totalValue, quantityValue]) => quantityValue !== 0
        ? totalValue / quantityValue
        : null,
      { id: "unit_value", unit: "currency" },
    );
    const curveClass = totalPercentage === null
      ? "curve-unavailable"
      : totalPercentage < curveA
      ? "curve-A"
      : (totalPercentage < curveB ? "curve-B" : "curve-C");

    return (
      <div key={resourceIdentity(resource)} className={`accordion-item offcanvas-accordion-item curve ${curveClass}`}>
        <div>
          <div className="flex align-center justify-content-space-between gap-8">
            <div className="grid">
              <span
                className="resource-id margin-bottom-0 margin-top-12 accordion-label clickable-id"
                onClick={() => onShowDetail(resource)}
                name={resource.id}
              >
                Insumo - {resource.id}
              </span>
              <span className="accordion-title margin-top-12" title={resource.name}>
                {resource.name} - {resource.measurement_unit}
              </span>
            </div>
            <button
              type="button"
              className="toggle-offcanvas"
              aria-label={`Detalhar insumo ${resource.id}`}
              onClick={() => onShowDetail(resource)}
            >
              <i className="icon icon-width-24 icon-ellipsis-v" aria-hidden={true}></i>
            </button>
          </div>
        </div>
        <div className="grid gap-16 columns-5-laptop-l margin-top-12">
          {renderMetricRow(resource, "Quantidade ", quantityEnvelope, (value) => value.toFixed(2).replace(".", ","))}
          {renderMetricRow(resource, "Unitário ", unitEnvelope, formatNumberToCurrency)}
          {renderMetricRow(resource, "Total", totalEnvelope, formatNumberToCurrency)}
          {renderMetricRow(resource, "Porcentagem", percentageEnvelope, (value) => `${value}%`)}
          {renderMetricRow(resource, "Porcentagem Acumulada", totalPercentageEnvelope, (value) => `${value.toFixed(2)}%`)}
        </div>
      </div>
    );
  });

  const fields = [
    { key: "name", label: "Descrição do ID" },
    { key: "id", label: "ID" },
    { key: "measurement_unit", label: "Unidade de medida" },
    { key: "percentage", label: "Porcentagem do ID" },
    { key: "totalPercentage", label: "Porcentagem total atual" },
    { key: "evaluated_quantity", label: "Quantidade Orçada" },
    { key: "evaluated_quantity_status", label: "Status da Quantidade Orçada" },
    { key: "evaluated_total", label: "Total Orçado" },
    { key: "evaluated_total_status", label: "Status do Total Orçado" },
    { key: "evaluated_indexed", label: "Orçado Indexado" },
    { key: "attended_quantity", label: "Quantidade Apropriada" },
    { key: "attended_quantity_status", label: "Status da Quantidade Apropriada" },
    { key: "attended_total", label: "Total Apropriado" },
    { key: "attended_total_status", label: "Status do Total Apropriado" },
    { key: "attended_indexed", label: "Apropriado Indexado" },
    { key: "total_quantity", label: "Quantidade Total" },
    { key: "total_value", label: "Valor total" },
    { key: "percentage_status", label: "Status da Porcentagem do ID" },
    { key: "totalPercentage_status", label: "Status da Porcentagem Acumulada" },
  ];

  const resourcesForExport = resources.map((resource) => {
    const evaluatedQuantity = normalizeMetricEnvelope(metricEnvelope(resource, "evaluated_quantity"));
    const attendedQuantity = normalizeMetricEnvelope(metricEnvelope(resource, "attended_quantity"));
    const selectedTotal = normalizeMetricEnvelope(
      resource.metrics?.[selectedTotalField]
        ?? resource.metrics?.value
        ?? metricEnvelope(resource, selectedTotalField),
    );
    const evaluatedTotal = type === "has_evaluated"
      ? selectedTotal
      : normalizeMetricEnvelope(metricEnvelope(resource, "evaluated_total"));
    const attendedTotal = type === "has_attended"
      ? selectedTotal
      : normalizeMetricEnvelope(metricEnvelope(resource, "attended_total"));
    const percentage = normalizeMetricEnvelope(metricEnvelope(resource, "percentage"));
    const totalPercentage = normalizeMetricEnvelope(metricEnvelope(resource, "totalPercentage"));
    return {
      ...resource,
      evaluated_quantity: metricCsvValue(evaluatedQuantity),
      evaluated_quantity_status: evaluatedQuantity.status,
      evaluated_total: metricCsvValue(evaluatedTotal),
      evaluated_total_status: evaluatedTotal.status,
      attended_quantity: metricCsvValue(attendedQuantity),
      attended_quantity_status: attendedQuantity.status,
      attended_total: metricCsvValue(attendedTotal),
      attended_total_status: attendedTotal.status,
      percentage: metricCsvValue(percentage),
      percentage_status: percentage.status,
      totalPercentage: metricCsvValue(totalPercentage),
      totalPercentage_status: totalPercentage.status,
    };
  });
  const totalValueEnvelope = meta.totalValueMetric || {
    id: "curve_total_value",
    value: typeof meta.totalValue === "number" && Number.isFinite(meta.totalValue)
      ? meta.totalValue
      : null,
    status: typeof meta.totalValue === "number" && Number.isFinite(meta.totalValue)
      ? "ok"
      : "source_unavailable",
  };
  const totalValuePresentation = metricPresentation(totalValueEnvelope, {
    formatter: formatNumberToCurrency,
    reasonId: "metric-curve-total-value-reason",
  });

  return (
    <>
      {loading && <div className="margin-top-24"><Loader /></div>}
      <div className="resources-container">
        <div className="flex gap-16 graph-labels">
          <h1
            className={type === "has_evaluated" ? "active-graph" : ""}
            onClick={() => selectType("has_evaluated")}
          >
            Insumos orçados
          </h1>
          <h1
            className={type === "has_attended" ? "active-graph" : ""}
            onClick={() => selectType("has_attended")}
          >
            Insumos apropriados
          </h1>
        </div>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="flex justify-content-space-between">
            <input
              style={{ width: "30%" }}
              type="text"
              value={searchString}
              placeholder="Buscar por insumo"
              onChange={handleSearchString}
            />
            <button
              type="button"
              className="button button-accent margin-bottom-8"
              id="search-resources-button"
              onClick={(event) => downloadCsv(event, fields, resourcesForExport)}
            >
              Baixar .xlsx
            </button>
          </div>
        </form>

        {error && <p role="alert">{error}</p>}
        {!loading && !error && buildingWbs && resources.length === 0 && (
          <p>Nenhum insumo encontrado.</p>
        )}
        {!error && buildingWbs && (
          <div className="flex justify-content-space-between margin-top-16">
            <span>{meta.totalItems} insumos</span>
            <span>
              Total: <span aria-describedby={totalValuePresentation.describedBy}>
                {totalValuePresentation.text}
              </span>
              {!totalValuePresentation.available && (
                <span id={totalValuePresentation.describedBy} style={metricReasonStyle}>
                  {totalValuePresentation.reason}
                </span>
              )}
            </span>
          </div>
        )}
        {resources.length > 0 && (
          <div className="accordion margin-top-24">
            {renderRows()}
          </div>
        )}
        {meta.totalPages > 1 && (
          <div className="flex align-center justify-content-space-between margin-top-24">
            <button
              type="button"
              className="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Anterior
            </button>
            <span>Página {page} de {meta.totalPages}</span>
            <button
              type="button"
              className="button"
              disabled={page >= meta.totalPages || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Curve;
